import { IExtraInfo, parseExtraInfo } from "@base/helpers/extraInfo";
import { ISpecialPackage, ISpecialPackageDetail, ISpecialPackageDetailImplement, ISpecialServiceDetailItem } from "./patientSpecialPackage.interface";

/**
 * Business rules for "my packages" and the steps before the package booking form, taken from the
 * mobile spec (`dat-lich-goi-web-spec.md` §5-§11). Everything here is pure so it can be tested.
 */

export const isBlank = (value?: string | null) => !value || !value.trim();
export const up = (value?: string | null) => (value ?? "").trim().toUpperCase();

/** 1 / 1.0 / "1" / "true" / true read as 1, 2 / "2" as 2, anything else as 0. */
export const flagValue = (value: unknown): number => {
  if (value === true) {
    return 1;
  }

  if (typeof value === "number") {
    return Math.trunc(value);
  }

  if (typeof value === "string") {
    const text = value.trim().toLowerCase();

    if (text === "true") {
      return 1;
    }

    const parsed = Number(text);

    return text && Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  }

  return 0;
};

export const extraInfoOf = (owner?: { extra_info?: unknown }): IExtraInfo => parseExtraInfo(owner?.extra_info);

const textOf = (value: unknown) => (typeof value === "string" && value.trim() ? value.trim() : null);

/**
 * A localised string in `extra_info`: `{lang}_{key}` then `{key}_{lang}` for the UI language, then
 * vi, then en; then `key` as a `{vi, en}` object; then `key` as a plain string.
 */
export const localizedExtra = (dict: IExtraInfo, base: string, language?: string): string | null => {
  const languages = [language ?? "vi", "vi", "en"];

  for (const lang of languages) {
    for (const key of [`${lang}_${base}`, `${base}_${lang}`]) {
      const value = textOf(dict[key]);

      if (value) {
        return value;
      }
    }
  }

  const nested = dict[base];

  if (nested && typeof nested === "object") {
    for (const lang of languages) {
      const value = textOf((nested as Record<string, unknown>)[lang]);

      if (value) {
        return value;
      }
    }
  }

  return textOf(nested);
};

//! Dates - read on Vietnam time (UTC+7) whatever the browser's zone is.
const VN_OFFSET_MS = 7 * 3600 * 1000;
const DAY_MS = 86400000;

const vn = (y: number, m: number, d: number, hh = 0, mm = 0, ss = 0) => new Date(Date.UTC(y, m - 1, d, hh, mm, ss) - VN_OFFSET_MS);

const vnParts = (date: Date) => {
  const shifted = new Date(+date + VN_OFFSET_MS);

  return [shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate()] as const;
};

export const startOfVnDay = (date: Date) => {
  const [y, m, d] = vnParts(date);

  return vn(y, m, d);
};

export const endOfVnDay = (date: Date) => {
  const [y, m, d] = vnParts(date);

  return vn(y, m, d, 23, 59, 59);
};

export const addDays = (date: Date, days: number) => new Date(+date + days * DAY_MS);

/** `yyyy-MM-dd` of the Vietnam day. */
export const toVnYmd = (date: Date) =>
  vnParts(date)
    .map((part, index) => String(part).padStart(index ? 2 : 4, "0"))
    .join("-");

/** `dd/MM/yyyy` of the Vietnam day. */
export const toVnDmy = (date: Date) => toVnYmd(date).split("-").reverse().join("/");

/** Takes "yyyy-MM-dd", "yyyy-MM-dd HH:mm:ss", ISO, "dd/MM/yyyy" or epoch seconds/ms; null when unreadable. */
export const parseVnDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" || /^\d{10,13}$/.test(String(value))) {
    const epoch = Number(value);

    return new Date(epoch < 1e12 ? epoch * 1000 : epoch);
  }

  const text = String(value).trim();
  let matched = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);

  if (matched && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(text)) {
    return vn(+matched[1], +matched[2], +matched[3], +(matched[4] ?? 0), +(matched[5] ?? 0), +(matched[6] ?? 0));
  }

  matched = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (matched) {
    return vn(+matched[3], +matched[2], +matched[1]);
  }

  const parsed = new Date(text);

  return isNaN(+parsed) ? null : parsed;
};

/**
 * When a package or a use lapses: at the given time of its expiry day, or at 23:59:59 when there is
 * no time - so the expiry day itself can still be booked. No date means no limit.
 */
export const expiryCutoff = (date?: string | null, time?: string | null): Date | null => {
  const parsed = parseVnDate(date);

  if (!parsed) {
    return null;
  }

  const [y, m, d] = vnParts(parsed);
  const clock = (time ?? "").trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?/);

  return clock ? vn(y, m, d, +clock[1], +clock[2], +(clock[3] ?? 0)) : vn(y, m, d, 23, 59, 59);
};

//! Routing
export type PackageRoute = "GROUP_EXAM" | "INSURANCE" | "LEGACY";
export type GroupExamMode = "SELECTABLE" | "FIXED" | "NOT_CONFIGURED";

export const groupExamMode = (pkg: ISpecialPackage): GroupExamMode => {
  const extra = extraInfoOf(pkg);

  if ("service_item_confirm" in extra && extra.service_item_confirm !== null && extra.service_item_confirm !== undefined) {
    return flagValue(extra.service_item_confirm) === 1 ? "FIXED" : "SELECTABLE";
  }

  // A package missing the flag but carrying keys only group checkups have is one, read-only.
  if (extra.max_appointment_cancel_times != null || extra.half_day_session_quota_check_from != null) {
    return "FIXED";
  }

  return "NOT_CONFIGURED";
};

export const isGroupExamRoute = (pkg: ISpecialPackage) => groupExamMode(pkg) !== "NOT_CONFIGURED";
export const isInsuranceSeller = (pkg: ISpecialPackage) => up(pkg.seller) === "INSURANCE" || up(pkg.supported_site?.code) === "INSMART";
export const isAbicPackage = (pkg: ISpecialPackage) => up(pkg.supported_customer_group) === "ABIC";

/** The order matters: a group checkup is sold with `seller = INSURANCE` too. */
export const routePackage = (pkg: ISpecialPackage): PackageRoute => {
  if (isGroupExamRoute(pkg)) {
    return "GROUP_EXAM";
  }

  return isInsuranceSeller(pkg) ? "INSURANCE" : "LEGACY";
};

//! Uses ("lượt")
export const visitsOf = (service: ISpecialPackageDetail) => service.patient_special_package_detail_implements ?? [];

/** A use's status after correcting for cancelled appointments (D7), upper case. */
export type EffectiveVisitFn = (visit: ISpecialPackageDetailImplement) => string;

const FINAL_VISIT_STATUSES = ["COMPLETED", "IN_PROGRESS", "CANCELLED"];

/**
 * Cancelling an appointment leaves its use PENDING with the old `appt_key`, which would read as
 * "being booked" forever. Uses whose appointment turned out CANCELLED are treated as cancelled.
 */
export const makeEffectiveVisit =
  (cancelledApptKeys: Set<string> = new Set()): EffectiveVisitFn =>
  (visit) => {
    const status = up(visit.status);

    if (!isBlank(visit.appt_key) && !FINAL_VISIT_STATUSES.includes(status) && cancelledApptKeys.has(String(visit.appt_key).trim())) {
      return "CANCELLED";
    }

    return status;
  };

/** Uses whose appointment has to be looked up to know whether it was cancelled. */
export const apptKeysToVerify = (pkg: ISpecialPackage): string[] => {
  const keys = (pkg.patient_special_package_details ?? []).flatMap((service) =>
    visitsOf(service)
      .filter((visit) => !isBlank(visit.appt_key) && !FINAL_VISIT_STATUSES.includes(up(visit.status)))
      .map((visit) => String(visit.appt_key).trim()),
  );

  return Array.from(new Set(keys));
};

/**
 * Uses that count as "used" for a one-shot package. A voucher only means used on an e-voucher service:
 * booking services can carry a code the backend assigned up front (`need_claim_evoucher`), which the
 * app does not count either.
 */
export const bookedCount = (service: ISpecialPackageDetail, eff: EffectiveVisitFn) =>
  visitsOf(service).filter((visit) => {
    const status = eff(visit);

    if (status === "CANCELLED") {
      return false;
    }

    if (status === "COMPLETED" || status === "IN_PROGRESS") {
      return true;
    }

    return (isEVoucher(service) && !isBlank(visit.voucher)) || !isBlank(visit.appt_key);
  }).length;

export const completedCount = (service: ISpecialPackageDetail) => visitsOf(service).filter((visit) => up(visit.status) === "COMPLETED").length;

export const canBookPackage = (pkg: ISpecialPackage, eff: EffectiveVisitFn) => {
  if (["CANCELLED", "UNPAID", "COMPLETED"].includes(up(pkg.status))) {
    return false;
  }

  if (pkg.finish_after_first_use === true) {
    return (pkg.patient_special_package_details ?? []).reduce((total, service) => total + bookedCount(service, eff), 0) === 0;
  }

  return true;
};

/** A one-shot package narrows to the service used, else the one being booked, else shows all. */
export const visibleServices = (pkg: ISpecialPackage): ISpecialPackageDetail[] => {
  const all = pkg.patient_special_package_details ?? [];

  if (pkg.finish_after_first_use !== true) {
    return all;
  }

  const completed = all.filter(
    (service) => up(service.status) === "COMPLETED" || visitsOf(service).some((visit) => up(visit.status) === "COMPLETED"),
  );

  if (completed.length) {
    return completed;
  }

  const booked = all.filter((service) => visitsOf(service).some((visit) => !isBlank(visit.appt_key)));

  return booked.length ? booked : all;
};

//! Service actions
export type ActionKind =
  | { kind: "CALL"; info: string }
  | { kind: "EMAIL"; info: string }
  | { kind: "GOTO"; info: string }
  | { kind: "BOOKING_AND_CONFIRM" }
  | { kind: "BOOKING" }
  | { kind: "NONE" };

/** Matched by prefix: `GO_TO` before `GOTO`, `BOOKING_AND_CONFIRM` before `BOOKING`. */
export const actionKind = (service: ISpecialPackageDetail): ActionKind => {
  const raw = (service.action ?? "").trim();
  const upper = raw.toUpperCase();
  const after = (prefix: string) => raw.slice(prefix.length).replace(/^:/, "").trim();

  if (upper.startsWith("CALL_TO")) {
    return { kind: "CALL", info: after("CALL_TO") };
  }

  if (upper.startsWith("EMAIL_TO")) {
    return { kind: "EMAIL", info: after("EMAIL_TO") };
  }

  if (upper.startsWith("GO_TO")) {
    return { kind: "GOTO", info: after("GO_TO") };
  }

  if (upper.startsWith("GOTO")) {
    return { kind: "GOTO", info: after("GOTO") };
  }

  if (upper.startsWith("BOOKING_AND_CONFIRM")) {
    return { kind: "BOOKING_AND_CONFIRM" };
  }

  if (upper.startsWith("BOOKING")) {
    return { kind: "BOOKING" };
  }

  return { kind: "NONE" };
};

export const isContactAction = (action: ActionKind) => action.kind === "CALL" || action.kind === "EMAIL" || action.kind === "GOTO";

/** Contact actions only work while the package is in use or done. */
export const canRunContactAction = (pkg: ISpecialPackage) => ["COMPLETED", "IN_PROGRESS"].includes(up(pkg.status));

/** Where a contact action leads: `tel:` keeps digits only, a bare URL gets `https://`. */
export const contactActionHref = (action: ActionKind): string | null => {
  switch (action.kind) {
    case "CALL": {
      const digits = action.info.replace(/\D/g, "");

      return digits ? `tel:${digits}` : null;
    }
    case "EMAIL":
      return action.info ? `mailto:${action.info}` : null;
    case "GOTO":
      if (!action.info) {
        return null;
      }

      return /^[a-z][a-z0-9+.-]*:\/\//i.test(action.info) ? action.info : `https://${action.info}`;
    default:
      return null;
  }
};

/** The use that decides a service's card: the one with the highest sequence. */
export const lastVisit = (service: ISpecialPackageDetail) => {
  const sorted = [...visitsOf(service)].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

  return sorted.length ? sorted[sorted.length - 1] : null;
};

export const isEVoucher = (service: ISpecialPackageDetail) => up(service.type) === "EVOUCHER";

/** The claimed e-voucher code, if any use carries one. */
export const claimedVoucher = (service: ISpecialPackageDetail) =>
  visitsOf(service)
    .find((visit) => !isBlank(visit.voucher))
    ?.voucher?.trim() ?? null;

export type ServiceSlot =
  | { kind: "ACTION"; action: ActionKind }
  | { kind: "EVOUCHER" }
  | { kind: "IN_PROGRESS" }
  | { kind: "BOOKED" }
  /** `visit` null = a new use has to be created first. */
  | { kind: "BOOK"; visit: ISpecialPackageDetailImplement | null; confirm: boolean }
  | { kind: "NONE" };

/** What sits under a service card on the insurance and group checkup screens. */
export const serviceSlot = (pkg: ISpecialPackage, service: ISpecialPackageDetail, eff: EffectiveVisitFn): ServiceSlot => {
  const action = actionKind(service);

  if (isEVoucher(service)) {
    return { kind: "EVOUCHER" };
  }

  if (isContactAction(action)) {
    return { kind: "ACTION", action };
  }

  const last = lastVisit(service);
  const status = last ? eff(last) : null;

  if (status === "IN_PROGRESS") {
    return { kind: "IN_PROGRESS" };
  }

  if (last && status !== "COMPLETED" && status !== "CANCELLED" && !isBlank(last.appt_key)) {
    return { kind: "BOOKED" };
  }

  if (!canBookPackage(pkg, eff)) {
    return { kind: "NONE" };
  }

  const confirm = action.kind === "BOOKING_AND_CONFIRM";

  if (!last || status === "COMPLETED") {
    return { kind: "BOOK", visit: null, confirm };
  }

  if ((status === "PENDING" && isBlank(last.appt_key)) || status === "CANCELLED") {
    return { kind: "BOOK", visit: last, confirm };
  }

  return { kind: "NONE" };
};

/** Sequence for a use about to be created: one past the highest there is. */
export const nextVisitSequence = (service: ISpecialPackageDetail) =>
  visitsOf(service).reduce((max, visit) => Math.max(max, visit.sequence ?? 0), 0) + 1;

//! Visit history
export type VisitButton = "ACTION" | "BOOK" | "DETAIL" | undefined;

/** The button on one use in the history list (§11) - the first rule that holds wins. */
export const visitButton = (
  pkg: ISpecialPackage,
  service: ISpecialPackageDetail,
  visit: ISpecialPackageDetailImplement,
  eff: EffectiveVisitFn,
): VisitButton => {
  const packageStatus = up(pkg.status);
  const status = eff(visit);

  if (isContactAction(actionKind(service))) {
    return canRunContactAction(pkg) ? "ACTION" : undefined;
  }

  if (packageStatus === "CANCELLED" || packageStatus === "UNPAID") {
    return undefined;
  }

  if (canBookPackage(pkg, eff) && status !== "COMPLETED" && status !== "IN_PROGRESS" && (status === "CANCELLED" || isBlank(visit.appt_key))) {
    return "BOOK";
  }

  return isBlank(visit.appt_key) ? undefined : "DETAIL";
};

export type VisitStatusView = "COMPLETED" | "IN_PROGRESS" | "CANCELLED" | "BOOKED" | "NOT_DONE";

export const visitStatusView = (visit: ISpecialPackageDetailImplement, eff: EffectiveVisitFn): VisitStatusView => {
  const status = eff(visit);

  if (status === "COMPLETED" || status === "IN_PROGRESS" || status === "CANCELLED") {
    return status;
  }

  return isBlank(visit.appt_key) ? "NOT_DONE" : "BOOKED";
};

//! Booking window (D6) and the group checkup window (§10.2)
export interface BookingWindow {
  min: Date;
  max: Date | null;
  bookable: boolean;
}

/** "T+1" → 1. Like the app, only the digits are kept ("T-1" also reads 1). */
export const bookingConditionDays = (pkg: ISpecialPackage) => Number(String(extraInfoOf(pkg).booking_condition ?? "").replace(/\D/g, "")) || 0;

export const bookingWindow = (pkg: ISpecialPackage, visit: ISpecialPackageDetailImplement | null, today = new Date()): BookingWindow => {
  const extra = extraInfoOf(pkg);
  let min = startOfVnDay(addDays(today, bookingConditionDays(pkg)));
  const caps: Date[] = [];
  const packageCutoff = expiryCutoff(pkg.expired_date, pkg.expired_time);
  const visitCutoff = visit ? expiryCutoff(visit.expired_date, visit.expired_time) : null;

  if (packageCutoff) {
    caps.push(packageCutoff);
  }

  if (visitCutoff) {
    caps.push(visitCutoff);
  }

  if (routePackage(pkg) === "GROUP_EXAM") {
    const start = parseVnDate(extra.start_available_booking_times);
    const end = parseVnDate(extra.end_available_booking_times);

    if (start && start > min) {
      min = startOfVnDay(start);
    }

    if (end) {
      caps.push(endOfVnDay(end));
    }
  }

  const max = caps.length ? new Date(Math.min(...caps.map((date) => +date))) : null;

  return { min, max, bookable: !max || +min <= +max };
};

export type GroupWindowMessage =
  | { kind: "BACKEND"; text: string }
  | { kind: "CONDITION_CONFLICT"; minDate: string; days: number; maxDate: string }
  | { kind: "RANGE"; start: string; end: string }
  | { kind: "FROM"; start: string }
  | { kind: "TO"; end: string }
  | { kind: "NONE" };

/** Why a group checkup cannot be booked now, in the priority order of §10.2. */
export const groupWindowMessage = (pkg: ISpecialPackage, window: BookingWindow, language?: string): GroupWindowMessage => {
  const extra = extraInfoOf(pkg);

  for (const key of ["available_booking_times_message", "booking_time_message", "noti_booking_time"]) {
    const text = localizedExtra(extra, key, language);

    if (text) {
      return { kind: "BACKEND", text };
    }
  }

  const days = bookingConditionDays(pkg);

  if (days > 0 && window.max) {
    return { kind: "CONDITION_CONFLICT", minDate: toVnDmy(window.min), days, maxDate: toVnDmy(window.max) };
  }

  const start = parseVnDate(extra.start_available_booking_times);
  const end = parseVnDate(extra.end_available_booking_times);

  if (start && end) {
    return { kind: "RANGE", start: toVnDmy(start), end: toVnDmy(end) };
  }

  if (start) {
    return { kind: "FROM", start: toVnDmy(start) };
  }

  if (end) {
    return { kind: "TO", end: toVnDmy(end) };
  }

  return { kind: "NONE" };
};

//! Confirmation (BOOKING_AND_CONFIRM)
/** `confirmation_info` is `{"vi","en"}` JSON (UI language → vi → en) or a plain string. */
export const confirmationText = (pkg: ISpecialPackage, language?: string): string | null => {
  const raw = (pkg.confirmation_info ?? "").trim();

  if (!raw) {
    return null;
  }

  const parsed = parseExtraInfo(raw);

  if (Object.keys(parsed).length) {
    for (const lang of [language ?? "vi", "vi", "en"]) {
      const value = textOf(parsed[lang]);

      if (value) {
        return value;
      }
    }

    return null;
  }

  return raw;
};

export const needsConfirmation = (pkg: ISpecialPackage, service: ISpecialPackageDetail, language?: string) =>
  actionKind(service).kind === "BOOKING_AND_CONFIRM" && !!confirmationText(pkg, language);

//! Who the booking is for
export type FamilyMode = "SELF" | "SELF_OR_FAMILY" | "FAMILY_ONLY";

export const familyMode = (pkg: ISpecialPackage): FamilyMode => {
  const value = flagValue(extraInfoOf(pkg).allow_family_record);

  return value === 1 ? "SELF_OR_FAMILY" : value === 2 ? "FAMILY_ONLY" : "SELF";
};

/** The five fields an insurance booking needs on the patient record before it can go ahead. */
export const hasCompleteAddress = (record?: {
  patient_address?: string;
  administrative_province_code?: string;
  administrative_province_name?: string;
  administrative_ward_code?: string;
  administrative_ward_name?: string;
}) =>
  !!record &&
  [
    record.patient_address,
    record.administrative_province_code,
    record.administrative_province_name,
    record.administrative_ward_code,
    record.administrative_ward_name,
  ].every((value) => !isBlank(value));

//! Sites
/** Where an insurance or group checkup package can be booked: `linked_sites` with a code, else its own site. */
export const bookableSites = (pkg: ISpecialPackage) => {
  const linked = (pkg.linked_sites ?? []).filter((site) => !isBlank(site.code));

  if (linked.length) {
    return linked;
  }

  return pkg.site?.code ? [pkg.site] : [];
};

//! Group checkup items
const isItemActive = (item: ISpecialServiceDetailItem) => item.active !== false && item.is_active !== false;

/** Active items in `sequence` order, missing sequences last. */
export const activeItems = (items?: ISpecialServiceDetailItem[] | null) =>
  (items ?? [])
    .filter(isItemActive)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (a.item.sequence ?? Number.MAX_SAFE_INTEGER) - (b.item.sequence ?? Number.MAX_SAFE_INTEGER) || a.index - b.index)
    .map(({ item }) => item);

/** The service holding the group checkup catalogue: the first one with an active item. */
export const itemsHolder = (pkg: ISpecialPackage) =>
  (pkg.patient_special_package_details ?? []).find((service) => activeItems(service.items).length > 0);

export const isGroupExamPackage = (pkg: ISpecialPackage) => isGroupExamRoute(pkg) && !!itemsHolder(pkg);

/** How much the chosen items may add up to: amount minus discount; 0 = no limit. */
export const groupExamCap = (pkg: ISpecialPackage) => Math.max(0, Number(pkg.amount ?? 0) - Number(pkg.discount_amount ?? 0));

/** The ids to confirm: everything chosen, mandatory items included, no 0/null. */
export const selectedItemIds = (items: ISpecialServiceDetailItem[], chosenIds: number[]) =>
  items
    .filter((item) => item.mandatory || chosenIds.includes(Number(item.id)))
    .map((item) => Number(item.id))
    .filter((id) => Number.isFinite(id) && id > 0);

/** `AMOUNT_EXCEEDED` may sit in `message`, `error_code`, `code` or `error`. */
export const errorBodyHas = (body: unknown, needle: string) => {
  if (!body || typeof body !== "object") {
    return typeof body === "string" && body.toUpperCase().includes(needle);
  }

  const record = body as Record<string, unknown>;
  const nested = record.error && typeof record.error === "object" ? (record.error as Record<string, unknown>) : {};

  return [record.message, record.error_code, record.code, record.error, nested.message, nested.code].some(
    (value) => typeof value === "string" && value.toUpperCase().includes(needle),
  );
};

/** The "Hình thức" a service type is shown as - `""` and `ONLINE` read as online; e-vouchers and unknown types show none. */
export const displayServiceType = (type?: string | null) => {
  switch (up(type)) {
    case "":
    case "ONLINE":
    case "ONLINE_SERVICE":
      return "ONLINE_SERVICE";
    case "OFFLINE":
    case "OFFLINE_SERVICE":
      return "OFFLINE_SERVICE";
    case "HOMECARE":
    case "HOMECARE_SERVICE":
      return "HOMECARE_SERVICE";
    case "HOMECARE_OR_OFFLINE_SERVICE":
      return "HOMECARE_OR_OFFLINE_SERVICE";
    default:
      return undefined;
  }
};

/** Progress of a regular package's service: done uses against its quantity. */
export const serviceProgress = (service: ISpecialPackageDetail) => {
  const done = completedCount(service);
  const qty = Number(service.qty ?? 0);

  if (done === 0) {
    return { kind: "NOT_STARTED" as const, done, qty };
  }

  return done >= qty ? { kind: "DONE" as const, done, qty } : { kind: "IN_PROGRESS" as const, done, qty };
};
